import type { Accessory, DatePrice, FormattedMsrp } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useIntl } from "react-intl"
import { getJapanVehicleAndMiscPricingFields } from "src/components/inventoryInformation/pricingForm/japanVehicleAndMisc"
import { useConfig } from "src/hooks"
import type { InventoryInformationFormValues } from "../../inventoryAndIntakeForm"
import type { PricingFormProps } from "../PricingFormProps"
import { getAlwaysVatDeductibleBasicPricingFields } from "../alwaysVatDeductibleBasic/getAlwaysVatDeductibleBasicPricingFields"
import { getAustraliaNetPricePricingFields } from "../australiaNetPrice/getAustraliaNetPricePricingFields"
import { getBasicPricingFields } from "../basic/getBasicPricingFields"
import { getCanadaPriceBreakdownPricingFields } from "../canadaPriceBreakdown/getCanadaPriceBreakdownPricingFields"
import { getExtraCostsPriceBreakdownPricingFields } from "../extraCostsPriceBreakdown"

export const usePricingForm = ({
	availableAccessories,
	formattedMsrp,
	inventoryId,
	isDraft,
	modelSeriesKey,
	priceHistory,
	values,
}: {
	availableAccessories: Accessory[] | undefined
	formattedMsrp?: FormattedMsrp
	inventoryId: string
	isDraft: boolean
	modelSeriesKey: string | undefined
	priceHistory: DatePrice[] | undefined
	values: Partial<InventoryInformationFormValues>
}): PricingFormProps => {
	const { formatMessage } = useIntl()

	const { editListingPriceNewCars, vatDeductibleAvailable } = useConfig()

	return useMemo<PricingFormProps>(() => {
		switch (values.pricingUseCase) {
			case "BASIC":
			case undefined:
				return {
					fields: getBasicPricingFields(isDraft, formatMessage, vatDeductibleAvailable),
					formattedMsrp,
					priceHistory,
					pricingUseCase: "BASIC",
				}
			case "ALWAYS_VAT_DEDUCTIBLE_BASIC":
				return {
					fields: getAlwaysVatDeductibleBasicPricingFields(
						values.dataSource,
						isDraft,
						formatMessage,
						editListingPriceNewCars,
						vatDeductibleAvailable,
					),
					formattedMsrp,
					priceHistory,
					pricingUseCase: "ALWAYS_VAT_DEDUCTIBLE_BASIC",
				}
			case "AUSTRALIA_NET_PRICE":
				return {
					fields: getAustraliaNetPricePricingFields(isDraft, formatMessage),
					priceHistory,
					pricingUseCase: "AUSTRALIA_NET_PRICE",
				}
			case "CANADA_PRICE_BREAKDOWN":
				return {
					fields: getCanadaPriceBreakdownPricingFields(
						isDraft,
						availableAccessories,
						modelSeriesKey,
						formatMessage,
					),
					inventoryId,
					priceHistory,
					pricingUseCase: "CANADA_PRICE_BREAKDOWN",
				}
			case "JAPAN_VEHICLE_AND_MISC":
				return {
					fields: getJapanVehicleAndMiscPricingFields(isDraft, formatMessage),
					priceHistory,
					pricingUseCase: "JAPAN_VEHICLE_AND_MISC",
				}
			case "EXTRA_COSTS_PRICE_BREAKDOWN":
				return {
					fields: getExtraCostsPriceBreakdownPricingFields({
						dataSource: values.dataSource,
						editListingPriceNewCars,
						formatMessage,
						isDraft,
						isNewCar: values.vehicleType === "NEW_CAR",
						vatDeductibleAvailable,
					}),
					formattedMsrp,
					inventoryId,
					priceHistory,
					pricingUseCase: "EXTRA_COSTS_PRICE_BREAKDOWN",
				}
		}
	}, [
		inventoryId,
		values,
		isDraft,
		formatMessage,
		editListingPriceNewCars,
		priceHistory,
		availableAccessories,
		modelSeriesKey,
		formattedMsrp,
		vatDeductibleAvailable,
	])
}
